import React, { useEffect, useRef, useState } from 'react';

/**
 * ScrollReveal component to animate elements as they enter the viewport.
 * Uses IntersectionObserver for high performance.
 */
export default function ScrollReveal({
  children,
  className = '',
  delay = 0,
  duration = 800,
  direction = 'up', // 'up' | 'down' | 'left' | 'right' | 'none'
  threshold = 0.05,
}) {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          if (ref.current) {
            observer.unobserve(ref.current);
          }
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -60px 0px', // Triggers slightly before element enters viewport
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold]);

  const getTransformClass = () => {
    if (isIntersecting) {
      return 'opacity-100 translate-x-0 translate-y-0 scale-100';
    }

    const base = 'opacity-0';
    switch (direction) {
      case 'up':
        return `${base} translate-y-12 scale-[0.98]`;
      case 'down':
        return `${base} -translate-y-12 scale-[0.98]`;
      case 'left':
        return `${base} translate-x-12 scale-[0.98]`;
      case 'right':
        return `${base} -translate-x-12 scale-[0.98]`;
      case 'none':
        return `${base} scale-[0.95]`;
      default:
        return `${base} translate-y-12`;
    }
  };

  const style = {
    transitionProperty: 'opacity, transform',
    transitionDuration: `${duration}ms`,
    transitionDelay: `${delay}ms`,
    transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)', // Premium easeOutExpo transition
  };

  return (
    <div
      ref={ref}
      style={style}
      className={`${getTransformClass()} ${className}`}
    >
      {children}
    </div>
  );
}
